import { Injectable, OnDestroy } from '@angular/core';
import { LocalStorageService } from '@df/browser/storage/local-storage.service';
import { IProductDataV2 } from '@df/catalog/product/product';
import { ProductResource } from '@df/catalog/product/product.resource';
import { Checkout } from '@df/checkout/checkout.interfaces';
import { App } from '@df/core/app';
import { Basket } from '@df/session/api/basket/basket';
import { TranslationService } from '@df/ui/translation/translation.service';
import { Subscription } from 'rxjs';
import { Product } from '../product.decorator';

declare module '@df/session/api/basket/basket-item' {
    interface IBasketItemData {
        /**
         * True if product is added as a free gift
         */
        //is_free_gift?: boolean;
        //master_delivery_method_bs?: string | undefined;
        delivery_lead_time?: Date;
        //use_additional_delivery_method?: 1 | 2;
    }
}

declare module '@tomandco/nexus-api-types' {
    namespace Nexus.Lib.General {
        interface AppData {
            delivery_time_label: string;
        }
    }
}

interface ILeadTime {
    leadtime: number | undefined;
    noInStockLabelOverwrite?: boolean;
}
export const ITEM_STATUS_CURRENT_ID = 5572;
@Injectable({
    providedIn: 'root'
})
export class ProductStockDescriptionService implements OnDestroy {
    protected subscriptions = new Subscription();

    constructor(
        private translationService: TranslationService,
        private basket: Basket,
        private app: App,
        private productResource: ProductResource,
        private localStorageService: LocalStorageService
    ) {
        this.subscriptions.add(this.app.events.on(Checkout.EVENTS.SubmitBefore).subscribe(() => this.saveBasketLeadTimes()));
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    private leadtime(qty: number, product: Product<IProductDataV2>, forceProductSource: boolean = false): ILeadTime | undefined {
        const quoteQty = product.getStockQty(false, undefined);
        if (quoteQty < qty || qty === 0) {
            const basketItem = this.basket.items.find(item => item.product?.id === product.id);
            // if Qty is 0, and basket item exist we calculate eta only for items in basket
            // and can use basket item ETA because it comes form customer api provided by BE
            // that is more accurate and up to date than ETA calculated based on data from reindexed product.
            if (basketItem && qty === 0 && !forceProductSource) {
                const eta = basketItem.data.delivery_lead_time;
                if (eta) {
                    const etaDate = new Date(eta);
                    const etaTime = etaDate && etaDate.getTime();
                    if (etaTime) {
                        return {
                            leadtime: this.getWeeksFromDays((etaTime - Date.now()) / (1000 * 60 * 60 * 24)),
                            noInStockLabelOverwrite: !!this.basket.items.find(i => !i.product?.data?.in_stock_lead_time_label)
                        };
                    }
                }
            } else {
                const backordersQty = qty - quoteQty;
                const firstPoQty =
                    this.firstPoQty(product) && this.firstPoTimeInWeeks(product) ? Math.min(this.firstPoQty(product), backordersQty) : 0;
                if (backordersQty <= firstPoQty) {
                    return { leadtime: this.firstPoTimeInWeeks(product) };
                } else {
                    return { leadtime: this.leadtimeInWeeks(product) };
                }
            }
        }
        return undefined;
    }

    private firstPoTimeInWeeks(product: Product<IProductDataV2>): number | undefined {
        const eta = product?.data?.po_in_stock_eta;
        if (eta) {
            const etaDate = new Date(eta);
            const etaTime = etaDate && etaDate.getTime();
            if (etaTime) {
                return this.getWeeksFromDays((etaTime - Date.now()) / (1000 * 60 * 60 * 24));
            }
        }
        return undefined;
    }

    private leadtimeInWeeks(product: Product<IProductDataV2>): number | undefined {
        const leadtime = product?.data?.leadtime;
        if (leadtime) {
            return this.getWeeksFromDays(leadtime);
        }
        return undefined;
    }
    private getWeeksFromDays(days: number): number {
        return Math.ceil(days / 7);
    }
    private getS(qty: number): string {
        return qty > 1 ? 's' : '';
    }
    private firstPoQty(product: Product<IProductDataV2>): number {
        return product?.data?.first_po_quantity || 0;
    }

    private translate(value: string, ...param: (number | string)[]): string {
        return this.translationService.transform(value, ...param);
    }

    isInStock(qty: number, product: Product<IProductDataV2>): boolean {
        if (qty && product?.isSalable(qty, undefined)) {
            const stockQty = Math.max(product.getStockQty(false, undefined), 0);
            return qty <= stockQty;
        }
        return false;
    }

    get(qty: number, productId: number, forceProductSource: boolean = false): string {
        const product = <Product<IProductDataV2>>this.productResource.get(productId);
        if ((qty || qty === 0) && product?.isSalable(qty, undefined)) {
            const leadtime = this.leadtime(qty, product, forceProductSource);
            const weeks = leadtime?.leadtime;
            return weeks && weeks > 1
                ? this.isMadeToOrder(productId)
                    ? this.translate(`Made for you in % week${this.getS(weeks)}`, weeks)
                    : this.translate(`Delivered in % week${this.getS(weeks)}${this.isInStock(qty, product) ? '*' : ''}`, weeks)
                : leadtime?.noInStockLabelOverwrite
                  ? this.app.data.delivery_time_label
                  : product?.data?.in_stock_lead_time_label || this.app.data.delivery_time_label;
        }
        return '';
    }

    isMadeToOrder(productId: number): boolean {
        const product = <Product<IProductDataV2>>this.productResource.get(productId);
        return (
            product.data?.item_status === ITEM_STATUS_CURRENT_ID &&
            this.firstPoQty(product) === 0 &&
            product.getStockQty(true, undefined, true) > 0 &&
            product.getStockQty(false, undefined, false) === 0
        );
    }

    saveBasketLeadTimes(): void {
        let basketLeadTimes: (string | undefined)[] = [];
        basketLeadTimes = this.basket.items.map(item => {
            const leadtime = item.product?.id ? this.get(item.qty, item.product?.id) : undefined;
            return leadtime;
        });
        this.localStorageService.set(`basketLeadTimes`, basketLeadTimes);
    }

    deleteBasketLeadTimes(): void {
        this.localStorageService.set(`basketLeadTimes`, null);
    }

    getBasketLeadTimes(): string[] {
        return this.localStorageService.get(`basketLeadTimes`);
    }
}
